import { defineStore } from 'pinia'

const ACTIVE_ONLINE_WORKSPACE_ID_KEY = 'activeOnlineWorkspaceId'
const ACTIVE_ONLINE_GALLERY_ID_KEY = 'activeOnlineGalleryId'
const ACTIVE_WORKSPACE_ID_KEY = 'activeWorkspaceId'
const ACTIVE_GALLERY_ID_KEY = 'activeGalleryId'

const INIT_KEY = 'mygallery-init'

export const useWorkspaceManagerStore = defineStore(
  'workspaces-manager',
  () => {

    const curationStore = useCurationStore()
    const workspace = useWorkspace()
    const workspaceStore = useWorkspaceStore()
    const workspaceSessionStore = useWorkspaceSessionStore()
    const { isJa } = useI18n()

    const _favoredColor: Ref<string> = ref('')
    const _favoredName: Ref<string> = ref('')
    const _activeOnlineWorkspaceId: Ref<string> = ref(lsGet(ACTIVE_ONLINE_WORKSPACE_ID_KEY))
    const _activeOnlineGalleryId: Ref<string> = ref(lsGet(ACTIVE_ONLINE_GALLERY_ID_KEY))
    const _activeWorkspaceId: Ref<string> = ref(lsGet(ACTIVE_WORKSPACE_ID_KEY))
    const _activeGalleryId: Ref<string> = ref(lsGet(ACTIVE_GALLERY_ID_KEY))

    const favoredColor = computed({
      get: () => _favoredColor.value,
      set: v => _favoredColor.value = v,
    })

    const favoredName = computed({
      get: () => _favoredName.value,
      set: v => _favoredName.value = v,
    })

    const activeOnlineWorkspaceId = computed({
      get: () => _activeOnlineWorkspaceId.value,
      set: v => {
        lsSet(ACTIVE_ONLINE_WORKSPACE_ID_KEY, v)
        _activeOnlineWorkspaceId.value = v
      }
    })

    const activeOnlineGalleryId = computed({
      get: () => _activeOnlineGalleryId.value,
      set: v => {
        lsSet(ACTIVE_ONLINE_GALLERY_ID_KEY, v)
        _activeOnlineGalleryId.value = v
      }
    })

    const activeWorkspaceId = computed({
      get: () => _activeWorkspaceId.value,
      set: v => {
        lsSet(ACTIVE_WORKSPACE_ID_KEY, v)
        _activeWorkspaceId.value = v
      }
    })

    const activeGalleryId = computed({
      get: () => _activeGalleryId.value,
      set: v => {
        lsSet(ACTIVE_GALLERY_ID_KEY, v)
        _activeGalleryId.value = v
      }
    })

    const initWorkspace = async () =>{
      try {
        activeWorkspaceId.value = await workspaceStore.addWorkspace(
          isJa.value ? '最初のギャラリー' : 'First Gallery'
        )
      } catch (e) { }
      await lsSet(INIT_KEY, true)
    }

    //初期化
    const initialized = lsGet(INIT_KEY)
    if (!initialized) {
      initWorkspace()
    }

    //ログインユーザ設定
    workspaceSessionStore.getDefaultConf().then((conf) => {
      if (!conf) {
        updateFavored('', '#' + randomColor())
        workspaceSessionStore.setDefaultConf(
          favoredName.value,
          favoredColor.value
        )
      } else {
        favoredName.value = conf.name
        favoredColor.value = conf.color
      }
    })

    //オンラインと同期
    workspaceStore.getAll().then((list) => {
      list
        .filter((we) => we.onlineId)
        .forEach((we) => {
          workspaceStore.syncOnlineCurations(we.id)
        })
    })

    const addPage = async (): Promise<string> => {
      const galleries = await curationStore.listGalleries(
        activeWorkspaceId.value
      )
      const nextSort =
        galleries.length === 0
          ? 1
          : Math.max(...galleries.map((g) => g.sort)) + 1
      const curation: Curation = {
        title: {
          ja: `${ isJa.value ? '新しいページ' : 'New page' }${ nextSort }`,
        },
        curationType: 'GALLERY',
        parts: [],
      }
      await curationStore.addGallery(curation, nextSort, activeWorkspaceId.value)
      return curation.id;
    }

    /**
     *
     * 既存のセッションを開く
     *
     * @param workspaceId
     * @returns ワークスペースの状態とワークスペースユーザー
     */
    const openSession = async (
      workspaceId: string
      ): Promise<{ state: WorkspaceState, user: WorkspaceUser }> => {

      const session: WorkspaceSessionEntry = await workspaceSessionStore.getSession(workspaceId)
      if (session) {
        try {
          const user: WorkspaceUser = {
            color: session.color,
            name: session.name,
            id: session.userId,
          }
          const state = await workspace.syncWorkspaceUser({
            workspaceId: workspaceId,
            user,
            wsUserKey: session.sessionKey,
          })
          return {
            state,
            user,
          }
        } catch (e) {
          throw new Error('invalid-session')
        }
      } else {
        throw new Error('no-session')
      }
    }

    /**
     *
     * 新しくセッションを開く
     *
     * @param workspaceId
     * @param password
     * @param wsUser
     * @returns
     */
    const createSession = async (
      workspaceId: string,
      password: string,
      wsUser: WorkspaceUser
      ): Promise<{ state: WorkspaceState, user: WorkspaceUser }> => {

      try {
        const user = clone(wsUser)
        const session: WorkspaceSessionEntry = await workspaceSessionStore.getSession(workspaceId)
        if (session) {
          user.id = session.userId
        } else {
          user.id = hashId() //新規ログインの場合、新しいランダムユーザIDを付与
        }
        //ログインの試行
        const state = await workspace.loginWorkspace({
          workspaceId: workspaceId,
          user,
          wsUserKey: password,
        })
        if (session) {
          //すでにセッションがある場合は、セッションキーだけ更新
          await workspaceSessionStore.updateSession(workspaceId, {
            sessionKey: state.key,
          })
        } else {
          //新しいセッション情報の作成
          await workspaceSessionStore.createSession(
            workspaceId,
            state.key,
            user.id,
            user.name,
            user.color
          )
        }
        updateFavored(user.name, user.color)
        return {
          state,
          user,
        }
      } catch (e) {
        throw new Error()
      }
    }

    const closeSession = async (workspaceId: string, deleteSession?: boolean) => {
      let session: WorkspaceSessionEntry = await workspaceSessionStore.getSession(workspaceId)
      if (session) {
        await workspace.logoutWorkspace({
          workspaceId,
          wsUserKey: session.sessionKey,
          user: { id: session.userId },
        })
        if (deleteSession) {
          await workspaceSessionStore.deleteSession(workspaceId)
        }
      }
    }

    const updateFavored = (name: string, color: string) => {
      favoredColor.value = color
      favoredName.value = name
      workspaceSessionStore.setDefaultConf(
        favoredName.value,
        favoredColor.value
      )
    }

    return {
      addPage,
      favoredColor,
      favoredName,
      activeOnlineWorkspaceId,
      activeOnlineGalleryId,
      activeWorkspaceId,
      activeGalleryId,
      openSession,
      createSession,
      closeSession,
      updateFavored,
    }

  },
  { persist: false },
)
